import { ScribeFolder } from 'v2/components/navigation/SideBar/Folders/FolderTree/FolderTree';

const sortFoldersAlphabetically = (folders: ScribeFolder[]): ScribeFolder[] => {
  return folders.sort((a, b) => a?.name.toLowerCase().localeCompare(b.name.toLowerCase()));
};

export const findParentIdInTree = (tree: ScribeFolder | null, id: string): string | null => {
  if (!tree || !tree.children) {
    return null;
  }
  const found = tree.children.find((item) => item.id === id);

  if (found) {
    return tree.id;
  }

  let retVal: string | null = null;

  tree.children
    .filter((item) => item.children)
    .forEach((subTree) => {
      const childId = findParentIdInTree(subTree, id);

      if (childId) {
        retVal = childId;
      }
    });

  return retVal;
};

export const getChildWithId = (tree: ScribeFolder | null, id: string): ScribeFolder | null => {
  if (!tree || !tree.children) {
    return null;
  }
  const found = tree.children.find((item) => item.id === id);

  if (found) {
    return found;
  }

  let retVal: ScribeFolder | null = null;

  tree.children
    .filter((item) => item.children)
    .forEach((subTree) => {
      const child = getChildWithId(subTree, id);

      if (child) {
        retVal = child;
      }
    });

  return retVal;
};

export const removeNodeWithId = (tree: ScribeFolder | null, id: string): ScribeFolder | null => {
  if (!tree) {
    return null;
  }
  if (!tree.children) {
    return tree;
  }
  const filtered = tree.children.filter((item) => {
    return item.id !== id;
  });

  if (filtered.length !== tree.children.length) {
    return { ...tree, children: filtered };
  }

  return {
    ...tree,
    children: tree.children.map((child) => removeNodeWithId(child, id))
  };
};

export const removeNodeWithIdFromArray = (folders: ScribeFolder[], id: string): ScribeFolder[] => {
  const newTree = removeNodeWithId({ children: folders, id }, id);

  return newTree.children || [];
};

export const insertChildIntoFolder = (
  tree: ScribeFolder | null,
  child: ScribeFolder | null,
  parentId: string,
  index: number
): ScribeFolder | null => {
  if (!tree) {
    return null;
  }

  if (tree.id === parentId) {
    const children = tree.children || [];
    const prev = children.slice(0, index);
    const post = children.slice(index);

    return {
      ...tree,
      children: [...prev, child, ...post]
    };
  }

  if (!tree.children) {
    return tree;
  }

  return {
    ...tree,
    children: tree.children.map((innerChild) => insertChildIntoFolder(innerChild, child, parentId, index))
  };
};

export const dropInFolder = (folders: ScribeFolder[], dragId: string, dropId: string, dropIndex: number): ScribeFolder[] => {
  let tree: ScribeFolder = { children: folders, id: null };
  const item = getChildWithId(tree, dragId);

  tree = removeNodeWithId(tree, dragId);

  tree = insertChildIntoFolder(tree, item, dropId, dropIndex);

  return tree?.children || [];
};

export const insertChildIntoFolderAlphabetically = (
  tree: ScribeFolder | null,
  child: ScribeFolder | null,
  parentId: string
): ScribeFolder | null => {
  if (!tree) {
    return null;
  }

  if (!child) {
    return tree;
  }

  if (tree.id === parentId) {
    const newChildren = [...(tree.children || []), child];

    sortFoldersAlphabetically(newChildren);
    return {
      ...tree,
      children: newChildren
    };
  }

  if (!tree.children) {
    return tree;
  }

  const newChildren = tree.children.map((innerChild) => insertChildIntoFolderAlphabetically(innerChild, child, parentId));

  sortFoldersAlphabetically(newChildren);
  return {
    ...tree,
    children: newChildren
  };
};

export const dropInFolderAlphabetically = (folders: ScribeFolder[], dragId: string, dropId: string): ScribeFolder[] => {
  let tree: ScribeFolder = { children: folders, id: null };
  const item: ScribeFolder | null = getChildWithId(tree, dragId);

  tree = removeNodeWithId(tree, dragId);

  tree = insertChildIntoFolderAlphabetically(tree, item, dropId);

  return tree.children || [];
};

export const reorder = <T>(list: T[], startIndex: number, endIndex: number): T[] => {
  if (startIndex === endIndex) {
    return list;
  }

  if (startIndex < 0 || startIndex >= list.length) {
    return list;
  }

  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);
  return result;
};

export const reorderChildren = (tree: ScribeFolder | null, parentId: string, dragId: string, dropIndex: number): ScribeFolder | null => {
  if (!tree) {
    return null;
  }
  if (!tree.children) {
    return tree;
  }

  if (tree.id === parentId) {
    const dragIndex = tree.children.findIndex((item) => item.id === dragId);

    if (dragIndex < dropIndex) {
      dropIndex--;
    }
    return {
      ...tree,
      children: reorder(tree.children, dragIndex, dropIndex)
    };
  }

  return {
    ...tree,
    children: tree.children.map((child) => reorderChildren(child, parentId, dragId, dropIndex))
  };
};

export const dropAtIndex = (folders: ScribeFolder[], dragId: string, dropId: string, dropIndex: number): ScribeFolder[] => {
  let tree: ScribeFolder = { children: folders, id: null };
  const dropParentId = findParentIdInTree(tree, dropId);
  const dragParentId = findParentIdInTree(tree, dragId);

  if (dragParentId === dropParentId) {
    tree = reorderChildren(tree, dropParentId, dragId, dropIndex);
  } else {
    const item = getChildWithId(tree, dragId);

    tree = removeNodeWithId(tree, dragId);
    tree = insertChildIntoFolder(tree, item, dropParentId, dropIndex);
  }

  return tree.children || [];
};

const editSingleFolder = (folder: ScribeFolder | null, newFolder: Partial<ScribeFolder>, id?: string): ScribeFolder => {
  if (!folder) {
    return null;
  }

  if ((id || folder.id) === newFolder.id) {
    return {
      ...folder,
      ...newFolder
    };
  }
  if (folder.children) {
    return {
      ...folder,
      children: sortFoldersAlphabetically(folder.children.map((inner) => editSingleFolder(inner, newFolder)))
    };
  }
  return folder;
};

export const editFolderInTree = (folders: ScribeFolder[], newFolder: Partial<ScribeFolder>, id?: string): ScribeFolder[] => {
  return sortFoldersAlphabetically(folders.map((folder) => editSingleFolder(folder, newFolder, id)));
};

export const addFolderToTree = (folders: ScribeFolder[], folder: ScribeFolder, parentId: string | null): ScribeFolder[] => {
  if (!parentId) {
    return sortFoldersAlphabetically([...folders, folder]);
  }
  const calculatedFolders = folders.map((existingFolder) => {
    const children = addFolderToTree(existingFolder.children || [], folder, existingFolder.id === parentId ? null : parentId);

    return {
      ...existingFolder,
      children: sortFoldersAlphabetically(children)
    };
  });

  return sortFoldersAlphabetically(calculatedFolders);
};
